import { useEffect, useCallback, useMemo, useState } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { useImageStore } from '@/entities/image/store';
import { getBackendUrl } from '@/shared/platform/web-adapter';

export default function ImageViewer() {
  const viewingImagePath = useImageStore((s) => s.viewingImagePath);
  const instanceId = useImageStore((s) => s.instanceId);
  const images = useImageStore((s) => s.images);
  const searchQuery = useImageStore((s) => s.searchQuery);
  const setViewingImagePath = useImageStore((s) => s.setViewingImagePath);
  const setActiveImagePath = useImageStore((s) => s.setActiveImagePath);
  const setSelectedIndex = useImageStore((s) => s.setSelectedIndex);

  const [scale, setScale] = useState(1.0);

  const filteredImages = useMemo(() => {
    if (!searchQuery) return images;
    const q = searchQuery.toLowerCase();
    return images.filter(img => img.name.toLowerCase().includes(q));
  }, [images, searchQuery]);

  const currentIndex = useMemo(() => {
    if (!viewingImagePath) return -1;
    return filteredImages.findIndex(img => img.path === viewingImagePath);
  }, [filteredImages, viewingImagePath]);

  const navigate = useCallback((direction: 'prev' | 'next') => {
    if (currentIndex === -1) return;
    let nextIdx = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    
    // Circular navigation
    if (nextIdx < 0) nextIdx = filteredImages.length - 1;
    if (nextIdx >= filteredImages.length) nextIdx = 0;

    const nextImage = filteredImages[nextIdx];
    if (nextImage) {
      setViewingImagePath(nextImage.path);
      // Sync global selection state for sidebar
      setActiveImagePath(nextImage.path);
      setSelectedIndex(nextIdx);
      setScale(1.0); // Reset scale
    }
  }, [currentIndex, filteredImages, setViewingImagePath, setActiveImagePath, setSelectedIndex]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') navigate('prev');
      if (e.key === 'ArrowRight') navigate('next');
      if (e.key === 'Escape') {
        setViewingImagePath(null);
        setScale(1.0);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [navigate, setViewingImagePath]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        setScale(s => {
          const newScale = e.deltaY < 0 ? s + 0.15 : s - 0.15;
          return Math.max(1.0, Math.min(newScale, 3.0));
        });
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, []);

  if (!viewingImagePath) return null;

  return (
    <div className="absolute inset-0 z-20 bg-zinc-950 flex flex-col group overflow-hidden">
      {/* Toolbar Layer */}
      <div className="absolute top-0 left-0 right-0 z-40 flex justify-end p-4 gap-2 pointer-events-none">
        <button
          onClick={() => setScale(s => Math.max(1.0, s - 0.25))}
          className="p-2 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-zinc-100 shadow-xl pointer-events-auto transition-colors"
          title="Zoom Out (-)"
        >
          <ZoomOut className="w-6 h-6" />
        </button>
        <button
          onClick={() => setScale(s => Math.min(3.0, s + 0.25))}
          className="p-2 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-zinc-100 shadow-xl pointer-events-auto transition-colors"
          title="Zoom In (+)"
        >
          <ZoomIn className="w-6 h-6" />
        </button>
        <button
          onClick={() => { setViewingImagePath(null); setScale(1.0); }}
          className="p-2 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-zinc-100 shadow-xl pointer-events-auto transition-colors"
          title="Close Viewer (Esc)"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Interactive Navigation Layer */}
      <div className="absolute inset-y-0 left-0 z-30 flex items-center px-4 pointer-events-none">
        <button
          onClick={() => navigate('prev')}
          className="p-3 rounded-full bg-zinc-800/50 hover:bg-zinc-700 text-zinc-100 transition-opacity pointer-events-auto"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      </div>

      <div className="absolute inset-y-0 right-0 z-30 flex items-center px-4 pointer-events-none">
        <button
          onClick={() => navigate('next')}
          className="p-3 rounded-full bg-zinc-800/50 hover:bg-zinc-700 text-zinc-100 transition-opacity pointer-events-auto"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      </div>

      {/* Image Area */}
      <div className="flex-1 flex items-center justify-center p-4 min-h-0 overflow-hidden">
        <img
          src={(() => {
            const img = filteredImages.find(i => i.path === viewingImagePath);
            if (img?.thumbnail) return img.thumbnail;
            if (instanceId && viewingImagePath) {
              const fname = viewingImagePath.split('/').pop();
              if (fname) {
                const backendUrl = getBackendUrl();
                return `${backendUrl}/api/serve/${instanceId}/${fname}`;
              }
            }
            return viewingImagePath ? `file://${viewingImagePath}` : '';
          })()}
          alt="Full view"
          className="max-w-full max-h-full object-contain pointer-events-none select-none transition-transform duration-100 ease-out"
          style={{ transform: `scale(${scale})` }}
        />
      </div>
      
      {/* Status Footer */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 px-3 py-1 bg-zinc-900/80 text-zinc-400 text-xs rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-40">
        <span>{currentIndex + 1} / {filteredImages.length}</span>
        <span>{Math.round(scale * 100)}%</span>
      </div>
    </div>
  );
}
